import * as React from 'react'
import { css, Rule } from 'glamor'
import * as PropTypes from 'prop-types'

import { UiSpacing } from '../../types'
import { useTheme } from '../../theme'

import { isKey } from '../../utils/index'

function removeUndefinedProps<T>(obj: T): Partial<T> {
  return Object.entries(obj).reduce(
    (acc, [key, value]) =>
      value === undefined || value === null
        ? acc
        : {
            ...acc,
            [key]: value,
          },
    {}
  )
}

export interface IFlexProps {
  alignContent?: 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' | 'stretch'
  alignItems?: 'flex-start' | 'flex-end' | 'center' | 'baseline' | 'stretch'
  alignSelf?: 'auto' | 'flex-start' | 'flex-end' | 'center' | 'baseline' | 'stretch'
  component?: string | React.ComponentType<{ ref?: React.RefObject<{}> | ((ref: HTMLDivElement) => void) }>
  display?: 'flex' | 'inline-flex'
  flexBasis?: number | string
  flexDirection?: 'row' | 'row-reverse' | 'column' | 'column-reverse'
  flexGrow?: number
  flexWrap?: 'wrap' | 'nowrap' | 'wrap-reverse'
  flexShrink?: number
  justifyContent?: 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' | 'space-evenly'
  marginTop?: UiSpacing
  marginRight?: UiSpacing
  marginBottom?: UiSpacing
  marginLeft?: UiSpacing
  paddingTop?: UiSpacing
  paddingRight?: UiSpacing
  paddingBottom?: UiSpacing
  paddingLeft?: UiSpacing
  role?: string
  order?: number
  row?: boolean
  style?: Rule
  width?: number | string
  maxWidth?: number | string
  minWidth?: number | string
  height?: number | string
  maxHeight?: number | string
  minHeight?: number | string
  children?: React.ReactNode
  innerRef?: React.RefObject<HTMLElement | HTMLDivElement> | ((ref: HTMLElement | HTMLDivElement) => void)
  onClick?: (event: React.SyntheticEvent) => void
  tabIndex?: number
  position?: 'relative' | 'absolute' | 'fixed' | 'sticky' | 'static'
}

export const Flex: React.FC<IFlexProps> = props => {
  const theme = useTheme()

  const {
    component: Component,
    children,
    display,
    alignItems,
    alignContent,
    alignSelf,
    flexBasis,
    justifyContent,
    flexDirection,
    flexWrap,
    flexShrink,
    style,
    flexGrow,
    row,
    innerRef,
    order,
    width,
    maxWidth,
    minWidth,
    height,
    maxHeight,
    minHeight,
    marginTop,
    marginRight,
    marginBottom,
    marginLeft,
    paddingTop,
    paddingRight,
    paddingBottom,
    paddingLeft,
    position,
    ...rest
  } = props

  const baseStyles = {
    alignContent,
    alignItems,
    alignSelf,
    display,
    flexShrink,
    flexBasis,
    flexDirection: row ? 'row' : flexDirection,
    flexGrow,
    flexWrap,
    justifyContent,
    order,
    position: position || 'relative',
    width,
    maxWidth,
    minWidth,
    height,
    maxHeight,
    minHeight,
    marginTop: isKey(theme.settings.spacing, marginTop) ? theme.settings.spacing[marginTop] : undefined,
    marginRight: isKey(theme.settings.spacing, marginRight) ? theme.settings.spacing[marginRight] : undefined,
    marginBottom: isKey(theme.settings.spacing, marginBottom) ? theme.settings.spacing[marginBottom] : undefined,
    marginLeft: isKey(theme.settings.spacing, marginLeft) ? theme.settings.spacing[marginLeft] : undefined,
    paddingTop: isKey(theme.settings.spacing, paddingTop) ? theme.settings.spacing[paddingTop] : undefined,
    paddingRight: isKey(theme.settings.spacing, paddingRight) ? theme.settings.spacing[paddingRight] : undefined,
    paddingBottom: isKey(theme.settings.spacing, paddingBottom) ? theme.settings.spacing[paddingBottom] : undefined,
    paddingLeft: isKey(theme.settings.spacing, paddingLeft) ? theme.settings.spacing[paddingLeft] : undefined,
  }

  const styles = style
    ? (Array.isArray(style) ? style : [style]).reduce((acc, styleObj) => Object.assign({}, acc, styleObj), baseStyles)
    : baseStyles

  if (!Component) {
    return null
  }

  return (
    <Component {...css(removeUndefinedProps(styles))} ref={innerRef} {...rest}>
      {children}
    </Component>
  )
}

Flex.propTypes = {
  alignContent: PropTypes.oneOf(['flex-start', 'flex-end', 'center', 'space-between', 'space-around', 'stretch']),
  alignItems: PropTypes.oneOf(['flex-start', 'flex-end', 'center', 'baseline', 'stretch']),
  alignSelf: PropTypes.oneOf(['auto', 'flex-start', 'flex-end', 'center', 'baseline', 'stretch']),
  component: PropTypes.string,
  display: PropTypes.oneOf(['flex', 'inline-flex']),
  flexBasis: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  flexDirection: PropTypes.oneOf(['row', 'row-reverse', 'column', 'column-reverse']),
  flexGrow: PropTypes.number,
  flexWrap: PropTypes.oneOf(['wrap', 'nowrap', 'wrap-reverse']),
  flexShrink: PropTypes.number,
  justifyContent: PropTypes.oneOf([
    'flex-start',
    'flex-end',
    'center',
    'space-between',
    'space-around',
    'space-evenly',
  ]),
  marginTop: PropTypes.oneOf(['xSmall', 'small', 'medium', 'large', 'xLarge', 'xxLarge']),
  marginRight: PropTypes.oneOf(['xSmall', 'small', 'medium', 'large', 'xLarge', 'xxLarge']),
  marginBottom: PropTypes.oneOf(['xSmall', 'small', 'medium', 'large', 'xLarge', 'xxLarge']),
  marginLeft: PropTypes.oneOf(['xSmall', 'small', 'medium', 'large', 'xLarge', 'xxLarge']),
  paddingTop: PropTypes.oneOf(['xSmall', 'small', 'medium', 'large', 'xLarge', 'xxLarge']),
  paddingRight: PropTypes.oneOf(['xSmall', 'small', 'medium', 'large', 'xLarge', 'xxLarge']),
  paddingBottom: PropTypes.oneOf(['xSmall', 'small', 'medium', 'large', 'xLarge', 'xxLarge']),
  paddingLeft: PropTypes.oneOf(['xSmall', 'small', 'medium', 'large', 'xLarge', 'xxLarge']),
  order: PropTypes.number,
  row: PropTypes.bool,
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  minWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  minHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  children: PropTypes.node,
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]) as any,
  position: PropTypes.oneOf(['relative', 'absolute', 'fixed', 'sticky', 'static']),
}

Flex.defaultProps = {
  alignItems: 'stretch',
  component: 'div',
  display: 'flex',
  flexDirection: 'column',
}
